import React, { useMemo, useState, useRef, useEffect } from 'react';
import {
    ResponsiveContainer,
    ScatterChart,
    Scatter,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    Label,
    ErrorBar
} from 'recharts';
import { ChartConfig, ScientificChartConfig } from '../../../../types';
import { DEFAULT_SCIENTIFIC_CONFIG, SCIENTIFIC_COLOR_PALETTES } from '../config/scientificDefaults';
import { ScientificMarker } from './ScientificMarker';

interface ErrorBarPlotProps {
    config: ChartConfig;
    scientificConfig: ScientificChartConfig;
    data: any[];
    onPointClick?: (point: any) => void;
}

/**
 * Error Bar Plot
 * Experimental-data style plot with vertical/horizontal error bars
 * Supports symmetric, asymmetric, constant, and field-based errors
 */
export const ErrorBarPlot: React.FC<ErrorBarPlotProps> = ({
    config,
    scientificConfig,
    data,
    onPointClick
}) => {
    const { fieldMappings, axisOptions, style, errorBars, legend, animation } = scientificConfig;
    const chartColor = config.customization?.color || '#f59e0b';

    // Track container dimensions with resize observer
    const containerRef = useRef<HTMLDivElement>(null);
    const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

    useEffect(() => {
        if (!containerRef.current) return;

        const updateDimensions = () => {
            if (containerRef.current) {
                const rect = containerRef.current.getBoundingClientRect();
                setDimensions({
                    width: Math.max(rect.width, 100),
                    height: Math.max(rect.height, 300)
                });
            }
        };

        updateDimensions();
        const resizeObserver = new ResizeObserver(updateDimensions);
        resizeObserver.observe(containerRef.current);

        return () => resizeObserver.disconnect();
    }, []);

    // Process data for error bar plot
    const processedData = useMemo(() => {
        console.log('[ErrorBarPlot] Processing data:', {
            dataLength: data?.length,
            xField: fieldMappings.xField,
            yField: fieldMappings.yField,
            errorBarsMode: errorBars.mode,
            sampleRow: data?.[0]
        });

        if (!data || data.length === 0 || !fieldMappings.xField || !fieldMappings.yField) {
            console.log('[ErrorBarPlot] Missing required fields or data');
            return [];
        }

        return data.map((row, idx) => {
            // Try both direct field access and 'x'/'y' from processed data
            const x = row[fieldMappings.xField!] ?? row.x;
            const y = row[fieldMappings.yField!] ?? row.y;

            if (x === undefined || y === undefined) return null;

            // Calculate error values
            // Helper to get numeric value from row, supporting both direct field and pre-processed fields
            const getNumericValue = (fieldName: string | undefined): number => {
                if (!fieldName) return 0;
                const val = row[fieldName] ?? row[fieldName.replace(/^.*\./, '')] ?? 0;
                return Math.abs(Number(val) || 0);
            };

            let yError = 0;
            let yErrorUpper = 0;
            let yErrorLower = 0;
            let xError = 0;
            let xErrorUpper = 0;
            let xErrorLower = 0;

            console.log('[ErrorBarPlot] Processing error for row', idx, {
                mode: errorBars.mode,
                yErrorField: fieldMappings.yErrorField,
                yErrorUpperField: fieldMappings.yErrorUpperField,
                yErrorLowerField: fieldMappings.yErrorLowerField,
                row
            });

            if (errorBars.mode === 'constant') {
                yError = errorBars.constantValue || 0;
                yErrorUpper = errorBars.constantValue || 0;
                yErrorLower = errorBars.constantValue || 0;
            } else if (errorBars.mode === 'field') {
                // ± error from single field
                yError = getNumericValue(fieldMappings.yErrorField);
                yErrorUpper = yError;
                yErrorLower = yError;
                xError = getNumericValue(fieldMappings.xErrorField);
                xErrorUpper = xError;
                xErrorLower = xError;
            } else if (errorBars.mode === 'bounds') {
                // Exact upper/lower bounds - error is the DIFFERENCE between bound and y value
                const upperVal = getNumericValue(fieldMappings.yErrorUpperField);
                const lowerVal = getNumericValue(fieldMappings.yErrorLowerField);
                const yNum = typeof y === 'number' ? y : 0;

                // If upper bound is set, calculate distance from y to upper
                if (fieldMappings.yErrorUpperField && upperVal) {
                    yErrorUpper = Math.abs(upperVal - yNum);
                }
                // If lower bound is set, calculate distance from lower to y  
                if (fieldMappings.yErrorLowerField && lowerVal) {
                    yErrorLower = Math.abs(yNum - lowerVal);
                }

                console.log('[ErrorBarPlot] Bounds mode:', { y: yNum, upperVal, lowerVal, yErrorUpper, yErrorLower });
            }

            // Handle direction modes
            if (errorBars.direction === 'positive') {
                yErrorLower = 0;
                xErrorLower = 0;
            } else if (errorBars.direction === 'negative') {
                yErrorUpper = 0;
                xErrorUpper = 0;
            }

            return {
                x: typeof x === 'number' ? x : idx,
                y: typeof y === 'number' ? y : 0,
                xOriginal: x,
                yOriginal: y,
                yError,
                yErrorUpper,
                yErrorLower,
                xError,
                xErrorUpper,
                xErrorLower,
                // For Recharts ErrorBar, we need [lower, upper] array format
                errorY: [yErrorLower, yErrorUpper],
                errorX: [xErrorLower, xErrorUpper],
                _originalRow: row
            };
        }).filter(Boolean) as any[];
    }, [data, fieldMappings, errorBars]);

    // Axis formatter
    const formatAxis = (value: number): string => {
        if (typeof value !== 'number') return String(value);
        if (axisOptions.axisFormat === 'scientific') {
            return value.toExponential(2);
        } else if (axisOptions.axisFormat === 'engineering') {
            const exp = Math.floor(Math.log10(Math.abs(value)) / 3) * 3;
            if (exp === 0 || !isFinite(exp)) return value.toFixed(2);
            return `${(value / Math.pow(10, exp)).toFixed(2)}e${exp}`;
        }
        return value.toLocaleString();
    };

    // Animation style
    const animationStyle = animation.fadeIn ? {
        animation: `fadeIn ${animation.duration}ms ease-out`
    } : {};

    console.log('[ErrorBarPlot] Final processedData:', {
        length: processedData.length,
        sample: processedData.slice(0, 3),
        allData: processedData
    });

    if (processedData.length === 0) {
        return (
            <div className="w-full h-full flex items-center justify-center text-gray-400">
                <div className="text-center">
                    <p>No data to display</p>
                    <p className="text-sm text-gray-500 mt-1">Map X, Y, and Error fields to create an error bar plot</p>
                </div>
            </div>
        );
    }

    const isXNumeric = processedData.length > 0 && typeof processedData[0].x === 'number';
    const hasXError = processedData.some(d => d.xError > 0 || d.xErrorUpper > 0 || d.xErrorLower > 0);
    const hasYError = processedData.some(d => d.yError > 0 || d.yErrorUpper > 0 || d.yErrorLower > 0);

    console.log('[ErrorBarPlot] Error bar visibility:', {
        hasYError,
        hasXError,
        sampleErrorValues: processedData[0] ? {
            yError: processedData[0].yError,
            yErrorUpper: processedData[0].yErrorUpper,
            yErrorLower: processedData[0].yErrorLower,
            errorY: processedData[0].errorY
        } : null,
        errorBarsConfig: { mode: errorBars.mode, constantValue: errorBars.constantValue, show: errorBars.show }
    });

    // Calculate domains with padding
    const calculateDomain = (axis: 'x' | 'y'): [number, number] => {
        if (!processedData.length) return [0, 100];

        // Filter valid values first
        const validData = processedData.filter(d =>
            typeof d[axis] === 'number' && Number.isFinite(d[axis])
        );

        if (!validData.length) return [0, 100];

        const values = validData.map(d => d[axis]);

        // Include error bars in range calculation
        if (axis === 'y') {
            const lowers = validData
                .map(d => d.y - (d.yErrorLower || 0))
                .filter(Number.isFinite);
            const uppers = validData
                .map(d => d.y + (d.yErrorUpper || 0))
                .filter(Number.isFinite);
            values.push(...lowers, ...uppers);
        } else if (axis === 'x') {
            const lowers = validData
                .map(d => d.x - (d.xErrorLower || 0))
                .filter(Number.isFinite);
            const uppers = validData
                .map(d => d.x + (d.xErrorUpper || 0))
                .filter(Number.isFinite);
            values.push(...lowers, ...uppers);
        }

        const min = Math.min(...values);
        const max = Math.max(...values);

        if (!Number.isFinite(min) || !Number.isFinite(max)) return [0, 100];

        if (min === max) {
            const pad = min === 0 ? 1 : Math.abs(min) * 0.1;
            return [min - pad, max + pad];
        }

        const range = max - min;
        return [min - range * 0.1, max + range * 0.1];
    };

    const xDomain = isXNumeric ? calculateDomain('x') : undefined;
    const yDomain = calculateDomain('y');

    console.log('[ErrorBarPlot] Calculated domains:', { xDomain, yDomain });

    // Use responsive container instead of manual calculation
    return (
        <div className="w-full h-full" style={{ ...animationStyle, minHeight: '350px', height: '100%' }}>
            <style>{`
                @keyframes fadeIn {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }
            `}</style>

            <ResponsiveContainer width="100%" height="100%" minHeight={350} debounce={50}>
                <ScatterChart
                    margin={{ top: 20, right: legend.show ? 120 : 30, bottom: 40, left: 60 }}
                >
                    <CartesianGrid
                        strokeDasharray={axisOptions.minorGrid ? "1 1" : "3 3"}
                        stroke={axisOptions.gridColor || '#333'}
                        strokeWidth={axisOptions.gridThickness || 1}
                        vertical={axisOptions.grid}
                        horizontal={axisOptions.grid}
                    />
                    <XAxis
                        type={isXNumeric ? 'number' : 'category'}
                        dataKey="x"
                        scale={isXNumeric && axisOptions.logScaleX ? 'log' : 'auto'}
                        domain={isXNumeric ? xDomain : undefined}
                        allowDataOverflow={true}
                        tick={{ fill: '#888', fontSize: 10 }}
                        tickFormatter={isXNumeric ? formatAxis : undefined}
                        stroke="#666"
                    >
                        <Label
                            value={axisOptions.labelX || fieldMappings.xField || 'X'}
                            position="bottom"
                            offset={15}
                            fill="#aaa"
                            fontSize={12}
                        />
                    </XAxis>
                    <YAxis
                        type="number"
                        scale={axisOptions.logScaleY ? 'log' : 'auto'}
                        domain={yDomain}
                        allowDataOverflow={true}
                        tick={{ fill: '#888', fontSize: 10 }}
                        tickFormatter={formatAxis}
                        stroke="#666"
                    >
                        <Label
                            value={axisOptions.labelY || fieldMappings.yField || 'Y'}
                            angle={-90}
                            position="left"
                            offset={25}
                            fill="#aaa"
                            fontSize={12}
                        />
                    </YAxis>
                    <Tooltip
                        contentStyle={{
                            backgroundColor: 'rgba(26, 26, 46, 0.95)',
                            border: '1px solid #f59e0b',
                            borderRadius: '6px',
                            padding: '8px 12px',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.5)'
                        }}
                        itemStyle={{ color: '#fff', fontSize: '13px' }}
                        cursor={{ stroke: '#ffffff33', strokeWidth: 1 }}
                        formatter={(value: number, name: string) => {
                            if (name === 'x' || name.includes('Error')) return [null, null];
                            return [formatAxis(value), name];
                        }}
                        labelFormatter={(label) => `X: ${typeof label === 'number' ? formatAxis(label) : label}`}
                    />
                    {legend.show && (
                        <Legend
                            layout="vertical"
                            align="right"
                            verticalAlign="middle"
                            wrapperStyle={{ paddingLeft: 20 }}
                        />
                    )}

                    {/* Data points with error bars */}
                    <Scatter
                        name={fieldMappings.yField || 'Value'}
                        data={processedData}
                        fill={chartColor}
                        isAnimationActive={false}
                        shape={(props) => (
                            <ScientificMarker
                                {...props}
                                shape={style.markerShape}
                                size={style.markerSize}
                                outline={style.markerOutline}
                                fill={chartColor}
                                alpha={style.alpha}
                            />
                        )}
                        onClick={(data) => onPointClick?.(data.payload)}
                    >
                        {/* Y Error Bars */}
                        {hasYError && (
                            <ErrorBar
                                dataKey="errorY"
                                width={errorBars.capSize}
                                strokeWidth={errorBars.lineThickness}
                                stroke={errorBars.color || chartColor}
                                direction="y"
                            />
                        )}
                        {/* X Error Bars */}
                        {hasXError && (
                            <ErrorBar
                                dataKey="errorX"
                                width={errorBars.capSize}
                                strokeWidth={errorBars.lineThickness}
                                stroke={errorBars.color || chartColor}
                                direction="x"
                            />
                        )}
                    </Scatter>
                </ScatterChart>
            </ResponsiveContainer>
        </div>
    );
};

export default ErrorBarPlot;
